import { HttpStatusCode } from '@angular/common/http';
import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { WireExtraPrompts, WireForm, WireTransactionDetails } from 'projects/money-movement-ui/src/app/create-request/models/form/TransactionForm';
import { ToastrService } from 'ngx-toastr';
import { selectHasAccessInitiateMoneyMovement } from 'projects/money-movement-ui/src/app/authorize/reducers';
import { MONTH_LIST } from 'projects/money-movement-ui/src/app/create-request/models/wire/MonthList';
import { selectIs401kToAccount, selectStagedTransactionForm, selectTransactionFormServiceError, selectWireCountries, selectWireInfo } from 'projects/money-movement-ui/src/app/create-request/reducers';
import { currencyStringToNumber } from 'projects/money-movement-ui/src/app/create-request/utilities/currencyStringToNumber';
import { selectInitReleaseTransactionInContext } from 'projects/money-movement-ui/src/app/initiate-and-release/reducers';
import { NamePipe } from 'projects/money-movement-ui/src/app/shared/pipes/NamePipe';
import { FormatIsoAddressFieldsService } from 'projects/money-movement-ui/src/app/shared/services/format-isoAddressFields.service';
import { amountTypeDict, bankIdentifierTypes, clearingSystemIdTypes, Constants, pageInContext, transactionDetailLabels, transactionTypes, transactionTypesDict, pdfLabelsSubmitDetails } from 'projects/money-movement-ui/src/app/shared/utilities/Constants';
import { selectViewInContext } from 'projects/money-movement-ui/src/app/shared/reducers';
import { CountryDescriptionFromCode } from 'projects/money-movement-ui/src/app/shared/utilities/CountryDescriptionFromCode';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { selectEAuthCustomers, selectFilteredWiresStandingAuth } from 'projects/money-movement-ui/src/app/account-info-header/reducers';
import { AdobeHelpers } from 'projects/money-movement-ui/src/app/shared/adobe-tagging/adobeHelper';
import { ReviewPageActions } from 'projects/money-movement-ui/src/app/create-request/actions';
import { AdobeConstants } from 'projects/money-movement-ui/src/app/shared/adobe-tagging/AdobeConstants';
import { Acknowledgement } from 'projects/money-movement-ui/src/app/create-request/models/review-page/ReviewPage';
import { BankDetails, BankSubDetails } from 'projects/money-movement-ui/src/app/standing-authorizations/models/BankDetailsModels';
import { CurrencyPipe } from '@angular/common';
import { Identifier } from 'projects/money-movement-ui/src/app/create-request/models/wire/Identifier';

@Component({
  standalone: false,
  selector: 'mm-transaction-details-wire-condensed',
  templateUrl: './transaction-details-wire-condensed.component.html',
  styleUrls: ['./transaction-details-wire-condensed.component.scss']
})
export class TransactionDetailsWireCondensedComponent implements OnInit, OnDestroy {
  readonly sectionTitle: string = 'Transaction details';
  readonly labels = transactionDetailLabels;
  readonly wireDisclaimer: string = Constants.wireReviewPageDisclaimer;
  readonly pdfLabelsSubmitDetails = pdfLabelsSubmitDetails;

  @Input() isIra: boolean = false;
  transmissionNotes: string = '';
  transmissionNotesLabel: string = this.labels.wireTransmissionNotes;
  pdfTransmissionNotesLoa: string = pdfLabelsSubmitDetails.additionalWireInstructions;
  transactionType: string = transactionTypesDict.wire;
  accountHolderRequestingTransaction: string = '';
  recipientBankInformation: BankDetails = new BankDetails();
  recipientBankAccountNumber: string = '';
  recipientBankAba: string = '';
  recipientBankIban: string = '';
  recipientBankClearingSystemId: string = '';
  usingStandingAuth: string = 'No';
  hasIntermediaryBank: string = 'No';
  processDate: string = '';
  wireAmount: string = '';
  totalWithdrawalAmount: string = '';
  wireFee: string = '';
  intermediaryAccountNumber: string = '';
  intermediaryIban: string = '';
  intermediaryBic: string = '';
  intermediaryClearingSystemId: string = '';
  intermediaryBankInformation: string[] = [];
  secondIntermediaryAccountNumber: string = '';
  secondIntermediaryIban: string = '';
  secondIntermediaryBic: string = '';
  secondIntermediaryClearingSystemId: string = '';
  secondIntermediaryBankInformation: string[] = [];
  thirdIntermediaryAccountNumber: string = '';
  thirdIntermediaryIban: string = '';
  thirdIntermediaryBic: string = '';
  thirdIntermediaryClearingSystemId: string = '';
  thirdIntermediaryBankInformation: string[] = [];
  isThreeStep: boolean = false;
  isFourStep: boolean = false;
  isIdentifierAba: boolean = false;
  disableEditLink: boolean = false;
  isInitiateAndReleaseFlowFromTable: boolean = false;
  is401kToAccount: boolean = false;
  grossNet: string = '';
  amountIncludingTaxes: string = '';
  isIrsAba: boolean = false;
  taxPayerId: string = '';
  taxTypeCode: string = '';
  taxYear: string = '';
  taxMonth: string = '';
  bankDetails: BankDetails[] = [];
  standingAuthDetails: string[] = [];
  isBundledStandingAuthTransaction: boolean = false;
  isConfirmationPage: boolean = false;
  labelRefNum: string = '';
  refNum: string = '';
  federalReferenceNumberDisclaimer: string = Constants.federalReferenceNumberDisclaimer;

  destroy$: Subject<void> = new Subject<void>();

  private store = inject(Store);
  private formatIsoAddressFieldsService = inject(FormatIsoAddressFieldsService);
  private toastr = inject(ToastrService);
  private namePipe = inject(NamePipe);
  private currencyPipe = inject(CurrencyPipe);

  ngOnInit(): void {
    combineLatest([
      this.store.select(selectStagedTransactionForm),
      this.store.select(selectWireInfo),
      this.store.select(selectWireCountries),
      this.store.select(selectEAuthCustomers),
      this.store.select(selectFilteredWiresStandingAuth),
    ]).pipe(takeUntil(this.destroy$))
      .subscribe(([form, wireInfo, countries, eAuthCustomers, standingAuth]) => {
        this.bankDetails = [];
        const details = form.transactionDetails as WireTransactionDetails;
        const countryMap = countries.map((country) => {
          return {
            value: country.code,
            name: country.description
          };
        });
        const accountHolderRequestingTransactionId = (form as WireForm).extraPrompts?.requestingAccountHolderId ?? '';
        if (!!accountHolderRequestingTransactionId) {
          const accountHolder = eAuthCustomers.find((cust) => cust.customerId === accountHolderRequestingTransactionId);
          const accountHolderName = accountHolder ? `${this.namePipe.transform(accountHolder.firstName)} ${this.namePipe.transform(accountHolder.lastName)}` : '';
          this.accountHolderRequestingTransaction = accountHolderName;
        }
        this.hasIntermediaryBank = details.intermediarySelection === 'no' ? 'No' : 'Yes';
        this.isIdentifierAba = details?.receivingBankIdentifierList?.[0]?.identifierType === 'USABA';
        const selectedAuthorization = form?.extraPrompts?.standingAuthId?.split('-')[1];
        const selectedStandingAuthorization = standingAuth.find((auths) => auths.cashieringRqstId === selectedAuthorization);
        const recipientIdentifierList = details?.receivingAccountHolderIdentifierList;
        const isUseStandingAuth = (form as WireForm).extraPrompts?.useStandingAuth === 'yes' || (form as WireForm).extraPrompts?.useBundledStandingAuth === 'yes';

        if (recipientIdentifierList && recipientIdentifierList?.length > 0) {
          for (const identifier of recipientIdentifierList) {
            if (identifier?.identifierType?.toLowerCase() === 'iban') {
              this.recipientBankIban = identifier?.identifierValue || '';
            } else if (identifier?.identifierType?.toLowerCase() === 'usaba') {
              this.recipientBankAba = identifier.identifierValue || '';
            } else {
              this.recipientBankAccountNumber = identifier.identifierValue || '';
            }
          }
        }
        const bankIdentifierList = details?.receivingBankIdentifierList;
        if (!this.recipientBankAba && bankIdentifierList && bankIdentifierList?.length > 0) {
          for (const identifier of bankIdentifierList) {
            if (identifier?.identifierType?.toUpperCase() === 'USABA') {
              this.recipientBankAba = identifier.identifierValue || '';
              break;
            }
          }
        }
        if (selectedStandingAuthorization) {
          this.standingAuthDetails = [
            selectedStandingAuthorization.nickname,
            selectedStandingAuthorization.to1,
            selectedStandingAuthorization.to2,
            selectedStandingAuthorization.to3,
            selectedStandingAuthorization.address
          ];
        }
        if (this.hasIntermediaryBank === 'No') {
          this.recipientBankInformation = this.getMappedBankDetail(
            this.getRecipientBankInformation(details),
            this.labels.bankInformation,
            'recipient-bank-information',
            false,
            'Recipient Bank',
            this.getMappedBankSubDetails(
              this.extractBankIdentifier(details.receivingBankIdentifierList, bankIdentifierTypes.accountNumber),
              this.extractBankIdentifier(details.receivingBankIdentifierList, bankIdentifierTypes.iban),
              this.extractBankIdentifier(details.receivingBankIdentifierList, bankIdentifierTypes.bic),
              this.extractBankClearingId(details.receivingBankIdentifierList),
              isUseStandingAuth ? this.extractBankIdentifier(details.receivingBankIdentifierList, bankIdentifierTypes.aba) : details.aba,
              'recipient-bank-sub-details'
            )
          );
        }
        this.bankDetails = [];
        // Recipient Bank is displayed as Intermediary Bank in Wire Transaction Details section for intermediary flows
        this.bankDetails.push(
          this.getMappedBankDetail(
            this.getRecipientBankInformation(details),
            this.labels.bankInformation,
            'intermediary-bank-information',
            true,
            'Intermediary',
            this.getMappedBankSubDetails(
              this.extractBankIdentifier(details.receivingBankIdentifierList, bankIdentifierTypes.accountNumber),
              this.extractBankIdentifier(details.receivingBankIdentifierList, bankIdentifierTypes.iban),
              this.extractBankIdentifier(details.receivingBankIdentifierList, bankIdentifierTypes.bic),
              this.extractBankClearingId(details.receivingBankIdentifierList),
              isUseStandingAuth ? this.extractBankIdentifier(details.receivingBankIdentifierList, bankIdentifierTypes.aba) : details.aba,
              'intermediary-bank-sub-details'
            )
          )
        );
        this.transactionType = this.getTransactionTypeLabel(form.extraPrompts as WireExtraPrompts);
        this.isBundledStandingAuthTransaction = (form as WireForm).extraPrompts?.useBundledStandingAuth === 'yes';
        this.usingStandingAuth = (form as WireForm).extraPrompts?.useStandingAuth === 'yes' || this.isBundledStandingAuthTransaction ? 'Yes' : 'No';
        if (this.hasIntermediaryBank === 'Yes') {
          const intermediaryCountry = details.intermediaryCountry.length === 2 ? CountryDescriptionFromCode(details.intermediaryCountry, countryMap) : details.intermediaryCountry;
          this.isThreeStep = details.isThreeStep || false;
          this.isFourStep = details.isFourStep || false;
          if (this.isThreeStep) {
            const secondIntermediaryCountry = details.secondIntermediaryCountry.length === 2 ? CountryDescriptionFromCode(details.secondIntermediaryCountry, countryMap) : details.secondIntermediaryCountry;
            this.bankDetails.push(
              this.getMappedBankDetail(
                this.getSecondIntermediaryBankInformation(details, secondIntermediaryCountry),
                this.labels.bankInformation,
                'second-intermediary-bank-information',
                true,
                'Second Intermediary',
                this.getMappedBankSubDetails(
                  details.secondIntermediaryAccountNumber || '',
                  details.secondIntermediaryIBAN || '',
                  details.secondIntermediaryBIC || '',
                  details.secondIntermediaryClearingSysId || '',
                  '',
                  'intermediary-bank-sub-details'
                )
              )
            );
          }
          if (this.isFourStep) {
            const thirdIntermediaryCountry = details.thirdIntermediaryCountry.length === 2 ? CountryDescriptionFromCode(details.thirdIntermediaryCountry, countryMap) : details.thirdIntermediaryCountry;
            this.bankDetails.push(
              this.getMappedBankDetail(
                this.thirdIntermediaryBankInformation = this.getThirdIntermediaryBankInformation(details, thirdIntermediaryCountry),
                this.labels.bankInformation,
                'third-intermediary-bank-information',
                true,
                'Third Intermediary',
                this.getMappedBankSubDetails(
                  details.thirdIntermediaryAccountNumber || '',
                  details.thirdIntermediaryIBAN || '',
                  details.thirdIntermediaryBIC || '',
                  details.thirdIntermediaryClearingSysId || '',
                  '',
                  'intermediary-bank-sub-details'
                )
              )
            );
          }
          // Intermediary bank is displayed as Recipient Bank in Wire Transaction Details section
          this.bankDetails.push(
            this.getMappedBankDetail(
              this.getIntermediaryBankInformation(details, intermediaryCountry),
              this.labels.bankInformation,
              'recipient-bank-information',
              true,
              'Recipient Bank',
              this.getMappedBankSubDetails(
                isUseStandingAuth ? this.extractBankIdentifier(details.intermediaryIdentifierList, bankIdentifierTypes.accountNumber) : details.intermediaryAccountNumber,
                this.extractBankIdentifier(details.intermediaryIdentifierList, bankIdentifierTypes.iban),
                this.extractBankIdentifier(details.intermediaryIdentifierList, bankIdentifierTypes.bic),
                this.extractBankClearingId(details.intermediaryIdentifierList),
                this.extractBankIdentifier(details.intermediaryIdentifierList, bankIdentifierTypes.aba),
                'recipient-bank-sub-details'
              )
            )
          );
        }
        this.wireFee = wireInfo.wireFee || 'Waived';
        this.processDate = details.selectedDate || '';
        this.wireAmount = details.amount || '';
        this.grossNet = details.amountType || '';
        this.amountIncludingTaxes = '';
        this.totalWithdrawalAmount = (currencyStringToNumber(wireInfo.wireFee) + currencyStringToNumber(details.amount)).toString();
        if (form.transactionDetails.amountType === amountTypeDict.gross) {
          this.wireAmount = wireInfo.withdrawalAmount;
          const withdrawalAmount = currencyStringToNumber(wireInfo.withdrawalAmount);
          this.amountIncludingTaxes = (
            withdrawalAmount
            - currencyStringToNumber(wireInfo.fedTaxAmount)
            - currencyStringToNumber(wireInfo.stateTaxAmount)
          ).toString();
          this.totalWithdrawalAmount = (
            currencyStringToNumber(this.wireFee) + withdrawalAmount
          ).toString();
          this.amountIncludingTaxes = this.currencyPipe.transform(this.amountIncludingTaxes, 'USD', 'symbol', '1.2-2') || '';
        } else if (form.transactionDetails.amountType === amountTypeDict.net) {
          const withdrawalAmount = currencyStringToNumber(wireInfo.wireAmount);
          this.amountIncludingTaxes = (
            withdrawalAmount
            + currencyStringToNumber(wireInfo.fedTaxAmount)
            + currencyStringToNumber(wireInfo.stateTaxAmount)
          ).toString();
          this.totalWithdrawalAmount = (
            currencyStringToNumber(this.wireFee) + +this.amountIncludingTaxes
          ).toString();
          this.amountIncludingTaxes = this.currencyPipe.transform(this.amountIncludingTaxes, 'USD', 'symbol', '1.2-2') || '';
        }
        this.isIrsAba = wireInfo.isIrsAba || false;
        this.taxPayerId = details.taxPayerId || '';
        this.taxTypeCode = details.taxTypeCode || '';
        this.taxYear = details.taxYear || '';
        const month = !!details.taxMonth ? `(${details.taxMonth})` : '';
        this.taxMonth = ((MONTH_LIST.filter((x) => x.code === details.taxMonth)[0]?.description || '') + ' ' + month).trim();
        this.transmissionNotes = details.transmissionNotes || '';
        this.transmissionNotesLabel = this.isIrsAba ? this.labels.wireIrsTransmissionNotes : this.labels.wireTransmissionNotes;
        this.pdfTransmissionNotesLoa = this.isIrsAba ? this.pdfLabelsSubmitDetails.transmissionNotesIrs : this.pdfLabelsSubmitDetails.additionalWireInstructions;
      });

    this.store.select(selectIs401kToAccount).pipe(takeUntil(this.destroy$))
      .subscribe((is401kToAccount) => {
        this.is401kToAccount = is401kToAccount;
      });


    // disable/enable edit link based on duplicate drafts lock and error type
    // copied almost directly from review-page.component.ts
    // this will need to be updated if Edit button logic in review-page.component.ts changes
    combineLatest([
      this.store.select(selectTransactionFormServiceError),
      this.store.select(selectHasAccessInitiateMoneyMovement),
      this.store.select(selectInitReleaseTransactionInContext)
    ]).pipe(takeUntil(this.destroy$))
      .subscribe(([error, hasMmInitiateAccess, initReleaseTransactionInContext]) => {
        this.isInitiateAndReleaseFlowFromTable = hasMmInitiateAccess && !!initReleaseTransactionInContext?.draftId;
        const isDuplicateDraftsConflict = this.isInitiateAndReleaseFlowFromTable;
        const hasConflictStatus = error.status === HttpStatusCode.Conflict;
        const hasAlreadyReleasedError = error.message === Constants.initReleaseAlreadyReleasedError
          || error.errorList?.some((err) => err.detail === Constants.initReleaseAlreadyReleasedError);
        if (hasConflictStatus || hasAlreadyReleasedError) {
          if (isDuplicateDraftsConflict) {
            if (hasConflictStatus) {
              this.toastr.error(Constants.initiateAndReleaseDuplicateDraftError, '', { toastClass: 'ngx-toastr toast-error tall-toastr' });
            }
            this.disableEditLink = true;
          } else {
            this.disableEditLink = false;
          }
        }
      });

    this.store.select(selectViewInContext).pipe(takeUntil(this.destroy$))
      .subscribe((step) => {
        if (step === pageInContext.confirmation) {
          this.isConfirmationPage = true;
          this.labelRefNum = Constants.federalReferenceNumber;
          this.refNum = Constants.pending;
        } else {
          this.isConfirmationPage = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  extractBankClearingId(identifiers: Identifier[]): string {
    const clearingId = identifiers?.find((id) => clearingSystemIdTypes.includes(id.identifierType.toUpperCase()));
    return clearingId ? clearingId.identifierValue : '';
  }

  extractBankIdentifier(identifiers: Identifier[], identifierType: string): string {
    const identifier = identifiers?.find((id) => id.identifierType.toLowerCase() === identifierType.toLowerCase());
    return identifier ? identifier.identifierValue : '';
  }

  getMappedBankDetail(address: string[], title: string, id: string, addBottomPadding: boolean, type: string, subDetailsArray: BankSubDetails[]): BankDetails {
    const bankDetails = new BankDetails();
    bankDetails.address = address;
    bankDetails.title = title;
    bankDetails.id = id;
    bankDetails.addBottomPadding = addBottomPadding;
    bankDetails.type = type;
    bankDetails.bankSubDetailsArray = subDetailsArray;
    return bankDetails;
  }

  getMappedBankSubDetails(accountNumber: string, iban: string, bic: string, clearingId: string, aba: string, id: string): BankSubDetails[] {
    const bankSubDetailsList: BankSubDetails[] = [];

    if (aba) {
      const abaDetail = new BankSubDetails();
      abaDetail.title = this.labels.abaNumber;
      abaDetail.data = aba;
      abaDetail.id = id + '-aba';
      abaDetail.addBottomPadding = !!accountNumber || !!clearingId;
      bankSubDetailsList.push(abaDetail);
    }
    if (bic) {
      const bicDetail = new BankSubDetails();
      bicDetail.title = this.labels.intermediaryUseSloaBicBankDetails;
      bicDetail.data = bic;
      bicDetail.id = id + '-bic';
      bicDetail.addBottomPadding = !!accountNumber || !!clearingId;
      bankSubDetailsList.push(bicDetail);
    }
    if (iban) {
      const ibanDetail = new BankSubDetails();
      ibanDetail.title = this.labels.iban;
      ibanDetail.data = iban;
      ibanDetail.id = id + '-iban';
      ibanDetail.addBottomPadding = !!accountNumber || !!clearingId;
      bankSubDetailsList.push(ibanDetail);
    }
    if (accountNumber) {
      const accountNumberDetail = new BankSubDetails();
      accountNumberDetail.title = this.labels.accountNumber;
      accountNumberDetail.data = accountNumber;
      accountNumberDetail.id = id + '-account-number';
      accountNumberDetail.addBottomPadding = !!clearingId;
      bankSubDetailsList.push(accountNumberDetail);
    }
    if (clearingId) {
      const clearingIdDetail = new BankSubDetails();
      clearingIdDetail.title = this.labels.clearingSystemId;
      clearingIdDetail.data = clearingId;
      clearingIdDetail.id = id + '-clearing-system-id';
      clearingIdDetail.addBottomPadding = false;
      bankSubDetailsList.push(clearingIdDetail);
    }

    return bankSubDetailsList;
  }

  getRecipientBankInformation(details: WireTransactionDetails): string[] {
    const receivingAddress = [];
    receivingAddress.push(details.receivingBankName || '');
    receivingAddress.push(
      this.formatIsoAddressFieldsService.getFieldsValue(details.receivingBankCity, details.receivingBankState)
      + this.formatIsoAddressFieldsService.getFieldsValue(details.receivingBankState, '')
    );
    receivingAddress.push(this.formatIsoAddressFieldsService.getFieldsValue(details.receivingBankCountry));
    return receivingAddress;
  }

  getIntermediaryBankInformation(details: WireTransactionDetails, country: string): string[] {
    const receivingAddress = [];
    receivingAddress.push(details.intermediaryName?.trim() || '');
    receivingAddress.push(this.formatIsoAddressFieldsService.getFieldsValue(details.intermediaryStreetNumber ? details.intermediaryStreetNumber + ' ' + details.intermediaryStreetName : details.intermediaryStreetName));
    receivingAddress.push(
      this.formatIsoAddressFieldsService.getFieldsValue(details.intermediaryApartmentSuiteEtc, details.intermediaryDepartment, details.intermediaryBuildingName, details.intermediaryFloor)
      + this.formatIsoAddressFieldsService.getFieldsValue(details.intermediaryDepartment, details.intermediaryBuildingName, details.intermediaryFloor)
      + this.formatIsoAddressFieldsService.getFieldsValue(details.intermediaryBuildingName, details.intermediaryFloor)
      + this.formatIsoAddressFieldsService.getFieldsValue(details.intermediaryFloor, '')
    );
    receivingAddress.push(
      this.formatIsoAddressFieldsService.getFieldsValue(details.intermediaryTownLocationName, details.intermediaryCity, details.intermediaryDistrict, details.intermediaryState, details.intermediaryZip)
      + this.formatIsoAddressFieldsService.getFieldsValue(details.intermediaryCity, details.intermediaryDistrict, details.intermediaryState, details.intermediaryZip)
      + this.formatIsoAddressFieldsService.getFieldsValue(details.intermediaryDistrict, details.intermediaryState, details.intermediaryZip)
      + this.formatIsoAddressFieldsService.getFieldsValue(details.intermediaryState, details.intermediaryZip)
      + this.formatIsoAddressFieldsService.getFieldsValue(details.intermediaryZip)
    );
    receivingAddress.push(country);
    return receivingAddress;
  }

  getSecondIntermediaryBankInformation(details: WireTransactionDetails, country: string): string[] {
    const receivingAddress = [];
    receivingAddress.push(details.secondIntermediaryName || '');
    receivingAddress.push(this.formatIsoAddressFieldsService.getFieldsValue(details.secondIntermediaryStreetNumber ? details.secondIntermediaryStreetNumber + ' ' + details.secondIntermediaryStreetName : details.secondIntermediaryStreetName));
    receivingAddress.push(
      this.formatIsoAddressFieldsService.getFieldsValue(details.secondIntermediaryApartmentSuiteEtc, details.secondIntermediaryDepartment, details.secondIntermediaryBuildingName, details.secondIntermediaryFloor)
      + this.formatIsoAddressFieldsService.getFieldsValue(details.secondIntermediaryDepartment, details.secondIntermediaryBuildingName, details.secondIntermediaryFloor)
      + this.formatIsoAddressFieldsService.getFieldsValue(details.secondIntermediaryBuildingName, details.secondIntermediaryFloor)
      + this.formatIsoAddressFieldsService.getFieldsValue(details.secondIntermediaryFloor, '')
    );
    receivingAddress.push(
      this.formatIsoAddressFieldsService.getFieldsValue(details.secondIntermediaryTownLocationName, details.secondIntermediaryCity, details.secondIntermediaryDistrict, details.secondIntermediaryState, details.secondIntermediaryZip)
      + this.formatIsoAddressFieldsService.getFieldsValue(details.secondIntermediaryCity, details.secondIntermediaryDistrict, details.secondIntermediaryState, details.secondIntermediaryZip)
      + this.formatIsoAddressFieldsService.getFieldsValue(details.secondIntermediaryDistrict, details.secondIntermediaryState, details.secondIntermediaryZip)
      + this.formatIsoAddressFieldsService.getFieldsValue(details.secondIntermediaryState, details.secondIntermediaryZip)
      + this.formatIsoAddressFieldsService.getFieldsValue(details.secondIntermediaryZip)
    );
    receivingAddress.push(country);
    return receivingAddress;
  }

  getThirdIntermediaryBankInformation(details: WireTransactionDetails, country: string): string[] {
    const receivingAddress = [];
    receivingAddress.push(details.thirdIntermediaryName || '');
    receivingAddress.push(this.formatIsoAddressFieldsService.getFieldsValue(details.thirdIntermediaryStreetNumber ? details.thirdIntermediaryStreetNumber + ' ' + details.thirdIntermediaryStreetName : details.thirdIntermediaryStreetName));
    receivingAddress.push(
      this.formatIsoAddressFieldsService.getFieldsValue(details.thirdIntermediaryApartmentSuiteEtc, details.thirdIntermediaryDepartment, details.thirdIntermediaryBuildingName, details.thirdIntermediaryFloor)
      + this.formatIsoAddressFieldsService.getFieldsValue(details.thirdIntermediaryDepartment, details.thirdIntermediaryBuildingName, details.thirdIntermediaryFloor)
      + this.formatIsoAddressFieldsService.getFieldsValue(details.thirdIntermediaryBuildingName, details.thirdIntermediaryFloor)
      + this.formatIsoAddressFieldsService.getFieldsValue(details.thirdIntermediaryFloor, '')
    );
    receivingAddress.push(
      this.formatIsoAddressFieldsService.getFieldsValue(details.thirdIntermediaryTownLocationName, details.thirdIntermediaryCity, details.thirdIntermediaryDistrict, details.thirdIntermediaryState, details.thirdIntermediaryZip)
      + this.formatIsoAddressFieldsService.getFieldsValue(details.thirdIntermediaryCity, details.thirdIntermediaryDistrict, details.thirdIntermediaryState, details.thirdIntermediaryZip)
      + this.formatIsoAddressFieldsService.getFieldsValue(details.thirdIntermediaryDistrict, details.thirdIntermediaryState, details.thirdIntermediaryZip)
      + this.formatIsoAddressFieldsService.getFieldsValue(details.thirdIntermediaryState, details.thirdIntermediaryZip)
      + this.formatIsoAddressFieldsService.getFieldsValue(details.thirdIntermediaryZip)
    );
    receivingAddress.push(country);
    return receivingAddress;
  }

  getTransactionTypeLabel(extraPrompts: WireExtraPrompts): string {
    const isBundledNonSloaTransaction = extraPrompts.useStandingAuth === 'bundled' && extraPrompts.useBundledStandingAuth !== 'yes';
    return isBundledNonSloaTransaction ? `${transactionTypesDict[transactionTypes.wire]} - bundled` : transactionTypesDict[transactionTypes.wire];
  }

  onEditClick(): void {
    this.store.dispatch(ReviewPageActions.clearAttentionMessage());
    AdobeHelpers.fireClick(AdobeConstants.LINKID + AdobeConstants._EDIT);
    this.store.dispatch(ReviewPageActions.editClicked());

    const acknowledgement: Acknowledgement = {
      isAcknowledgementReviewed: false,
      acknowledgementConsentTime: ''
    };
    this.store.dispatch(ReviewPageActions.updateAcknowledgement({ acknowledgement }));
  }

  // this is copied almost directly from review-page.component.ts
  // this will need to be updated if Edit button logic in review-page.component.ts changes
  get showEditLink(): boolean {
    return !(this.isInitiateAndReleaseFlowFromTable || this.is401kToAccount || this.isConfirmationPage);
  }
}
